// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { withRouter } from 'react-router';

import styled, { withTheme } from 'styled-components';
import FontIcon from 'material-ui/FontIcon/FontIcon';

import * as Routes from 'statsradio/helpers/constant/Routes';
import { getToken } from 'statsradio/modules/authentication/session/selectors/sessionSelectors';

import Radio from 'common/domain/radio';
import CampaignDashboardListing from 'common/modules/campaign/domain/CampaignDashboardListing';
import { Card, CardHeader, CardTitle } from 'statsradio/themes/v2/Stylesheet';
import Paging from 'statsradio/modules/pagination/domain/paging';
import DashboardCampaignApi from 'statsradio/modules/campaigns/services/DashboardCampaignApi';
import withSplashLoader from 'common/components/withSplashLoader';

import CampaignDashboardQuery from 'statsradio/modules/campaigns/domain/CampaignDashboardQuery';
import { CampaignStatusFilter } from 'statsradio/modules/campaigns/domain/CampaignV2Filters';
import CampaignsToLinkTable from './CampaignsToLinkTable';
import SwitchPageFooter from '../SwitchPageFooter';
import type { SalesMember } from '../../../salesMembers/domain/SalesMember';


const StyledTitle = styled(CardTitle)`
  flex: 1;

  display: flex;
  align-items: center;
`;

const ActionText = styled.div`
  display: flex;
  font-size: ${({ theme }) => theme.V2.fonts.button.size}px;
  align-items: center;
  &:hover {
    cursor: pointer;
  }
`;

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;

  width: 100%;
`;


const StyledSwitchPageFooter = styled(SwitchPageFooter)`
  padding: ${({ theme }) => theme.V2.metrics.margin.small}px;
`;

const NoCampaignMessage = styled.div`
  padding: ${({ theme }) => theme.V2.metrics.margin.small}px ${({ theme }) => theme.V2.metrics.margin.normal}px;
`;

const CardContent = withSplashLoader('isFetching', 'small')(styled.div``);

export const NO_CAMPAIGN_MESSAGE_ID = 'noCampaignMessage';
export const CAMPAIGN_TO_LINK_TABLE_ID = 'campaignToLinkTable';
export const GO_TO_UNLINKED_CAMPAIGN_LIST_ID = 'goToUnlinkedCampaignList';

interface Props {
  selectedSalesMember?: SalesMember;

  t: any;
  history: any;
  className?: any;

  radio: Radio;
  token: string;
}

interface State {
  paging: Paging;
  campaignsToLink: Array<CampaignDashboardListing>;
  isFetching: boolean;
  currentPage: number;
}

const DEFAULT_STATE = {
  isFetching: false,
  campaignsToLink: [],
  paging: new Paging({ currentPage: 1, numberOfPages: 1 }),
  currentPage: 1,
};

export class CampaignsToLinkCard extends React.Component<Props, State> {
  state = DEFAULT_STATE;

  componentDidMount() {
    this.fetchCampaignsToLink();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const radioChanged = !this.props.radio.isSame(prevProps.radio);
    const salesmanFilterChanged = this.props.selectedSalesMember !== prevProps.selectedSalesMember;
    const pageChanged = this.state.currentPage !== prevState.currentPage;
    const isOnFirstPage = this.state.currentPage === 1;

    if ((radioChanged || salesmanFilterChanged) && !isOnFirstPage) {
      this.onCurrentPageChange(1);
    } else if ((radioChanged || salesmanFilterChanged) && isOnFirstPage) {
      this.fetchCampaignsToLink();
    } else if (pageChanged) {
      this.fetchCampaignsToLink();
    }
  }

  async fetchCampaignsToLink() {
    const { token, radio } = this.props;
    this.setState({ isFetching: true });

    const salesmanReference = this.props.selectedSalesMember ? this.props.selectedSalesMember.reference : undefined;
    const query = new CampaignDashboardQuery({ page: this.state.currentPage, salesmanReference });
    const results = await DashboardCampaignApi.listUnlinkedCampaigns(token, radio.callSign, query);
    const paging = results.getPaging();

    this.setState({ campaignsToLink: results.items, isFetching: false, paging });
  }


  onCurrentPageChange = (page: number) => {
    this.setState({ currentPage: page });
  };

  render() {
    const { t, history, className } = this.props;
    const { isFetching, campaignsToLink, paging } = this.state;

    const shouldShowCampaignToLinkTable = campaignsToLink.length > 0;

    return (
      <StyledCard className={className}>
        <CardHeader>
          <StyledTitle>
            {t('toLink')}
          </StyledTitle>
          <ActionText
            id={GO_TO_UNLINKED_CAMPAIGN_LIST_ID}
            onClick={() => history.push({
              pathname: Routes.CAMPAIGNS,
              state: { statusFilter: CampaignStatusFilter.NOT_LINKED },
            })}
          >
            {t('allCampaigns')}
            <FontIcon style={{ fontSize: 'inherit', paddingLeft: '10px' }} className="material-icons">arrow_forward</FontIcon>
          </ActionText>
        </CardHeader>
        <CardContent isFetching={isFetching}>
          {!shouldShowCampaignToLinkTable &&
          <NoCampaignMessage id={NO_CAMPAIGN_MESSAGE_ID}>{t('noCampaignToLink')}</NoCampaignMessage>
          }
          {shouldShowCampaignToLinkTable &&
          <CampaignsToLinkTable
            id={CAMPAIGN_TO_LINK_TABLE_ID}
            campaigns={campaignsToLink}
          />
          }
        </CardContent>
        <StyledSwitchPageFooter
          paging={paging}
          onPageChange={this.onCurrentPageChange}
        />
      </StyledCard>
    );
  }
}

function mapStateToProps(state) {
  return {
    token: getToken(state),
    radio: state.session.radio,
  };
}

export default withTheme(translate('salesDashboard')(connect(mapStateToProps)(withRouter(CampaignsToLinkCard))));



// WEBPACK FOOTER //
// ./src/statsradio/modules/salesDashboard/components/campaignsToLink/CampaignsToLinkCard.js